import { resolveCellApiDomainSuffix } from '@/shared/cell-session';

/**
 * Outbound URL allowlists used immediately before `fetch()`.
 *
 * These functions are SSRF sanitizers: scheme + host are constrained to trusted
 * destinations before any network call. Prefer `fetchAllowedCognitoOAuth` /
 * `fetchAllowedCellGateway` at sinks. GitLab Advanced SAST may still flag
 * token-bearing request bodies; those sinks use an inline
 * `gitlab-advanced-sast-exclude: javascript-node-ssrf-generic-taint`.
 */

/** Cell IDs are lowercase alphanumeric segments separated by hyphens. */
const CELL_ID_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Cognito hosted UI / OAuth endpoints use this host shape.
 *
 * `auth-fips` is the GovCloud form: Cognito there is FIPS-endpoints-only and the
 * hosted UI is `{prefix}.auth-fips.{region}.amazoncognito.com`. Without the
 * optional group every GovCloud token/revoke/userInfo call is rejected here as a
 * disallowed host before it is ever made.
 */
const COGNITO_HOST_PATTERN = /^[a-z0-9-]+\.auth(?:-fips)?\.[a-z0-9-]+\.amazoncognito\.com$/i;

export type CognitoOAuthPath = '/oauth2/token' | '/oauth2/revoke' | '/oauth2/userInfo';

export function assertAllowedCellId(cellId: string): void {
  const trimmed = cellId.trim();
  if (!trimmed || !CELL_ID_PATTERN.test(trimmed)) {
    throw new Error('Invalid cell ID');
  }
}

export function assertSafePathSegment(segment: string, label: string): void {
  if (segment == null || /[\0\r\n@\\]/.test(segment) || segment.includes('://')) {
    throw new Error(`Invalid ${label}`);
  }
}

/** SSRF guard: HTTPS only, host must be `{cellId}.{api-suffix}` for this runtime. */
export function assertAllowedCellGatewayUrl(url: string, cellId: string): void {
  assertAllowedCellId(cellId);
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error('Invalid gateway URL');
  }
  if (parsed.protocol !== 'https:') {
    throw new Error('Gateway URL must use HTTPS');
  }
  const expectedHost = `${cellId.trim()}.${resolveCellApiDomainSuffix()}`;
  if (parsed.hostname !== expectedHost) {
    throw new Error('Gateway URL host mismatch');
  }
}

/**
 * Fetch a cell API Gateway URL after re-validating scheme/host at the sink.
 * Rebuilds the URL from validated parts so the sink cannot follow a tainted string.
 */
export async function fetchAllowedCellGateway(
  url: string,
  cellId: string,
  init?: RequestInit,
): Promise<Response> {
  assertAllowedCellGatewayUrl(url, cellId);
  const parsed = new URL(url);
  const safeUrl = `https://${parsed.hostname}${parsed.pathname}${parsed.search}`;
  // Destination is allowlisted above; request may carry auth tokens (not SSRF).
  return fetch(safeUrl, init); // gitlab-advanced-sast-exclude: javascript-node-ssrf-generic-taint
}

export function normalizeCognitoDomain(raw: string): string {
  const trimmed = raw.trim().replace(/^https?:\/\//i, '');
  const host = trimmed.split('/')[0]?.split(':')[0] ?? '';
  if (!host) {
    throw new Error('Missing Cognito domain');
  }
  return host;
}

export function assertAllowedCognitoHost(hostname: string): void {
  if (!COGNITO_HOST_PATTERN.test(hostname)) {
    throw new Error(`Disallowed Cognito host: ${hostname}`);
  }
}

/**
 * SSRF guard: HTTPS + Cognito hosted-UI host pattern + fixed OAuth path.
 * Domain input cannot steer scheme/host/path outside that allowlist.
 */
export function buildAllowedCognitoOAuthUrl(domain: string, path: CognitoOAuthPath): string {
  const hostname = normalizeCognitoDomain(domain);
  assertAllowedCognitoHost(hostname);
  return `https://${hostname}${path}`;
}

const COGNITO_OAUTH_PATHS: ReadonlySet<string> = new Set([
  '/oauth2/token',
  '/oauth2/revoke',
  '/oauth2/userInfo',
]);

/**
 * Fetch a Cognito OAuth endpoint after re-validating scheme/host/path at the sink.
 * Body/headers may carry tokens; only the destination URL is constrained here.
 */
export async function fetchAllowedCognitoOAuth(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error('Invalid Cognito OAuth URL');
  }
  if (parsed.protocol !== 'https:') {
    throw new Error('Cognito OAuth URL must use HTTPS');
  }
  assertAllowedCognitoHost(parsed.hostname);
  if (!COGNITO_OAUTH_PATHS.has(parsed.pathname) || parsed.search || parsed.hash) {
    throw new Error('Disallowed Cognito OAuth path');
  }
  // Rebuild from validated components only (no user-controlled scheme/host/path).
  const safeUrl = `https://${parsed.hostname}${parsed.pathname}`;
  // Destination is allowlisted above; request body may carry user tokens (not SSRF).
  return fetch(safeUrl, init); // gitlab-advanced-sast-exclude: javascript-node-ssrf-generic-taint
}
